import React, { useState } from 'react';
import { CreateImageItem, CreateTextItem } from '../../components';
import Folder from '../components/Folder';
import { AccentButton } from '../components/Button';
import ThemeSwitch from '../components/ThemeSwitch';
import TextItem from '../components/TextItem';
import ImageItem from '../components/ImageItem';
import FolderFilter from '../components/FolderFilter';
import MyFoldersMenu from '../components/MyFoldersMenu';
import ItemsFilter from '../components/ItemsFilter';
import SearchInput from '../components/SearchInput';
import DashboardMenu from '../components/DashboardMenu';
import GridIcon from '../icons/GridIcon';
import FolderIcon from '../icons/FolderIcon';
import UserIcon from '../icons/UserIcon';
import EditIcon from '../icons/EditIcon';

export default function NewDesignDemo() {
    const [selectedFolder, setSelectedFolder] = useState('newest-first');
    const [sortValue, setSortValue] = useState('newest-first');
    const [platformValue, setPlatformValue] = useState('all');
    const [searchValue, setSearchValue] = useState('');
    const [activeMenuItemId, setActiveMenuItemId] = useState('dashboard');
    
    const folderOptions = [
        { value: 'newest-first', label: 'Newest first' },
        { value: 'oldest-first', label: 'Oldest first' }
    ];

    const demoFolders = [
        { id: 1, name: 'Untitled folder', color: 'rgb(228, 99, 36)', fillColor: 'rgba(228, 99, 36, 0.2)' },
        { id: 2, name: 'myNew folder', color: 'rgb(74, 186, 98)', fillColor: 'rgba(74, 186, 98, 0.2)' }
    ];

    const dashboardMenuItems = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: GridIcon,
            children: [
                {
                    id: 'overview',
                    label: 'Overview',
                    icon: GridIcon,
                },
                {
                    id: 'analytics',
                    label: 'Analytics',
                    icon: GridIcon,
                }
            ]
        },
        {
            id: 'folders',
            label: 'Folders',
            icon: FolderIcon,
            children: [
                {
                    id: 'my-folders',
                    label: 'My Folders',
                    icon: FolderIcon,
                },
                {
                    id: 'shared-folders',
                    label: 'Shared Folders',
                    icon: FolderIcon,
                }
            ]
        },
        {
            id: 'profile',
            label: 'Profile',
            icon: UserIcon,
        },
        {
            id: 'settings',
            label: 'Settings',
            icon: EditIcon,
        }
    ];

    const handleFolderSelect = (folder) => {
        console.log('Selected folder:', folder);
    };

    const handleNewFolder = (name) => {
        console.log('Creating new folder:', name);
    };

    const handleMenuItemClick = (item) => {
        console.log('Clicked menu item:', item);
        setActiveMenuItemId(item.id);
    };

    return (
        <div className="p-4 min-h-screen bg-[#FFFFFF] dark:bg-[#121212]">
            <h1 className="text-2xl font-bold mb-6 text-[#121212] dark:text-[#D0D0D0]">New Design Demo</h1>
            
            {/* Dashboard Menu Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                <h2 className="text-lg font-semibold mb-4 text-[#121212] dark:text-[#D0D0D0]">Dashboard Menu</h2>
                <div className="flex items-start space-x-8">
                    <div className="flex-1">
                        <div className="w-64">
                            <DashboardMenu
                                items={dashboardMenuItems}
                                activeItemId={activeMenuItemId}
                                onItemClick={handleMenuItemClick}
                            />
                        </div>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-medium mb-2 text-[#7B7B7B] dark:text-[#848484]">State Variables</h3>
                        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                            <pre className="text-xs text-[#121212] dark:text-[#D0D0D0] whitespace-pre-wrap">
{JSON.stringify({
    activeMenuItemId,
    dashboardMenuItems
}, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Input Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                <h2 className="text-lg font-semibold mb-4 text-[#121212] dark:text-[#D0D0D0]">Search Input</h2>
                <div className="flex items-start space-x-8">
                    <div className="flex-1">
                        <div className="w-64">
                            <SearchInput 
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                placeholder="Search..."
                            />
                        </div>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-medium mb-2 text-[#7B7B7B] dark:text-[#848484]">State Variables</h3>
                        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                            <pre className="text-xs text-[#121212] dark:text-[#D0D0D0] whitespace-pre-wrap">
{JSON.stringify({
    searchValue
}, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>

            {/* Folder Filter Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                <h2 className="text-lg font-semibold mb-4 text-[#121212] dark:text-[#D0D0D0]">Folder Filter</h2>
                <div className="flex items-start space-x-8">
                    <div className="flex-1">
                        <FolderFilter 
                            value={selectedFolder}
                            onChange={(option) => setSelectedFolder(option.value)}
                            options={folderOptions}
                            placeholder="All folders"
                        />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-medium mb-2 text-[#7B7B7B] dark:text-[#848484]">State Variables</h3>
                        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                            <pre className="text-xs text-[#121212] dark:text-[#D0D0D0] whitespace-pre-wrap">
{JSON.stringify({
    selectedFolder,
    folderOptions
}, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>

            {/* My Folders Menu Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                <h2 className="text-lg font-semibold mb-4 text-[#121212] dark:text-[#D0D0D0]">My Folders Menu</h2>
                <div className="flex items-start space-x-8">
                    <div className="flex-1">
                        <MyFoldersMenu 
                            folders={demoFolders}
                            onFolderSelect={handleFolderSelect}
                            onNewFolder={handleNewFolder}
                        />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-medium mb-2 text-[#7B7B7B] dark:text-[#848484]">State Variables</h3>
                        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                            <pre className="text-xs text-[#121212] dark:text-[#D0D0D0] whitespace-pre-wrap">
{JSON.stringify({
    demoFolders
}, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>

            {/* Items Filter Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                <h2 className="text-lg font-semibold mb-4 text-[#121212] dark:text-[#D0D0D0]">Items Filter</h2>
                <div className="flex items-start space-x-8">
                    <div className="flex-1">
                        <ItemsFilter 
                            sortValue={sortValue}
                            platformValue={platformValue}
                            onSortChange={(option) => setSortValue(option.value)}
                            onPlatformChange={(option) => setPlatformValue(option.value)}
                        />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-medium mb-2 text-[#7B7B7B] dark:text-[#848484]">State Variables</h3>
                        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                            <pre className="text-xs text-[#121212] dark:text-[#D0D0D0] whitespace-pre-wrap">
{JSON.stringify({
    sortValue,
    platformValue
}, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>

            <h1 className="text-2xl font-bold mb-6 text-[#121212] dark:text-[#D0D0D0]">Modals</h1>

            {/* Add Block Component */}
            <div className="flex justify-center mb-6">
                <CreateImageItem 
                    imageUrl="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop&crop=center"
                    onCancel={() => console.log('Cancel clicked')}
                    onAdd={() => console.log('Add clicked')}
                    onFileSelect={() => console.log('File select clicked')}
                    onDraw={() => console.log('Draw clicked')}
                />
            </div>

            {/* Add Block Text Editor Component */}
            <div className="flex justify-center">
                <CreateTextItem 
                    content="console.log('Hello, world!');"
                    language="plain"
                    onCancel={() => console.log('Text Editor Cancel clicked')}
                    onAdd={() => console.log('Text Editor Add clicked')}
                    onFileSelect={() => console.log('Text Editor File select clicked')}
                />
            </div>

            <h1 className="text-2xl font-bold mb-6 text-[#121212] dark:text-[#D0D0D0] mt-12">Folder Components</h1>

            {/* Folder Components */}
            <div className="flex gap-6 flex-wrap">
                <Folder
                    name="My Documents"
                    itemCount={5}
                    date="02.02.25"
                    onEdit={() => console.log('Edit folder: My Documents')}
                    onDelete={() => console.log('Delete folder: My Documents')}
                />

                <Folder
                    name="Empty Folder"
                    itemCount={0}
                    date="01.15.25"
                    onEdit={() => console.log('Edit folder: Empty Folder')}
                    onDelete={() => console.log('Delete folder: Empty Folder')}
                />

                <Folder
                    name="Archive"
                    itemCount={99}
                    date="12.25.24"
                    onEdit={() => console.log('Edit folder: Archive')}
                    onDelete={() => console.log('Delete folder: Archive')}
                />

                <Folder
                    name="Very Long Folder Name That Might Overflow"
                    itemCount={12}
                    date="03.10.25"
                    onEdit={() => console.log('Edit folder: Very Long Folder Name')}
                    onDelete={() => console.log('Delete folder: Very Long Folder Name')}
                />
            </div>

            <h1 className="text-2xl font-bold mb-6 text-[#121212] dark:text-[#D0D0D0] mt-12">Button Components</h1>

            {/* Button Components */}
            <div className="space-y-8">
                {/* Primary Buttons */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-[#121212] dark:text-[#D0D0D0]">
                        Primary Buttons
                    </h2>
                    <div className="flex flex-wrap gap-4">
                        <AccentButton variant="primary" onClick={() => console.log('Primary clicked')}>
                            Primary Button
                        </AccentButton>
                        <AccentButton variant="primary" disabled>
                            Primary Disabled
                        </AccentButton>
                    </div>
                </div>

                {/* Secondary Buttons */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-[#121212] dark:text-[#D0D0D0]">
                        Secondary Buttons
                    </h2>
                    <div className="flex flex-wrap gap-4">
                        <AccentButton variant="secondary" onClick={() => console.log('Secondary clicked')}>
                            Secondary Button
                        </AccentButton>
                        <AccentButton variant="secondary" disabled>
                            Secondary Disabled
                        </AccentButton>
                    </div>
                </div>

                {/* Accent Buttons */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-[#121212] dark:text-[#D0D0D0]">
                        Accent Buttons
                    </h2>
                    <div className="flex flex-wrap gap-4">
                        <AccentButton variant="orange" onClick={() => console.log('Accent clicked')}>
                            Accent Button
                        </AccentButton>
                        <AccentButton variant="orange" disabled>
                            Accent Disabled
                        </AccentButton>
                    </div>
                </div>

                {/* All Variants Together */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-[#121212] dark:text-[#D0D0D0]">
                        All Variants
                    </h2>
                    <div className="flex flex-wrap gap-4">
                        <AccentButton variant="primary">Primary</AccentButton>
                        <AccentButton variant="secondary">Secondary</AccentButton>
                        <AccentButton variant="orange">Accent</AccentButton>
                    </div>
                </div>
            </div>

            <h1 className="text-2xl font-bold mb-6 text-[#121212] dark:text-[#D0D0D0] mt-12">Theme Switch Component</h1>

            {/* Theme Switch Component */}
            <div className="space-y-8">
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-[#121212] dark:text-[#D0D0D0]">
                        Theme Switch
                    </h2>
                    <div className="flex flex-wrap gap-4 items-center">
                        <div className="flex flex-col items-center space-y-2">
                            <span className="text-sm text-[#7B7B7B] dark:text-[#848484]">Light Mode</span>
                            <ThemeSwitch />
                        </div>
                    </div>
                </div>
            </div>

            <h1 className="text-2xl font-bold mb-6 text-[#121212] dark:text-[#D0D0D0] mt-12">TextItem Component</h1>

            {/* TextItem Component */}
            <div className="space-y-8">
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-[#121212] dark:text-[#D0D0D0]">
                        TextItem Component Variants
                    </h2>
                    <div className="flex flex-wrap gap-4">
                        <TextItem 
                            content={
                                <div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                    <div>sdfkww enjrwenwkkwwkjenrasdasd weffwef asfasfasfas</div>
                                </div>
                            }
                            date="02.02.25"
                            device="windows"
                            onCopy={(content) => console.log('Copied:', content)}
                            onEdit={() => console.log('Edit clicked')}
                            onDelete={() => console.log('Delete clicked')}
                        />
                        <TextItem 
                            content='  "name": "@rolldown/pluginutils",'
                            date="02.02.25"
                            device="android"
                            onCopy={(content) => console.log('Copied:', content)}
                            onEdit={() => console.log('Edit clicked')}
                            onDelete={() => console.log('Delete clicked')}
                        />
                        <TextItem 
                            content='  "version": "1.0.0-beta.27",'
                            date="02.02.25"
                            device="apple"
                            onCopy={(content) => console.log('Copied:', content)}
                            onEdit={() => console.log('Edit clicked')}
                            onDelete={() => console.log('Delete clicked')}
                        />
                        <TextItem 
                            content='  "license": "MIT",'
                            date="02.02.25"
                            device="windows"
                            onCopy={(content) => console.log('Copied:', content)}
                            onEdit={() => console.log('Edit clicked')}
                            onDelete={() => console.log('Delete clicked')}
                        />
                    </div>
                </div>
            </div>

            <h1 className="text-2xl font-bold mb-6 text-[#121212] dark:text-[#D0D0D0] mt-12">ImageItem Component</h1>

            {/* ImageItem Component */}
            <div className="space-y-8">
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-[#121212] dark:text-[#D0D0D0]">
                        ImageItem Component Examples
                    </h2>
                    <div className="flex flex-wrap gap-4">
                        <ImageItem 
                            imageUrl="https://picsum.photos/253/100?random=1"
                            imageAlt="Sample image 1"
                            date="02.02.25"
                            device="windows"
                            onCopy={(imageUrl) => console.log('Copied image URL:', imageUrl)}
                            onEdit={() => console.log('Edit clicked')}
                            onDelete={() => console.log('Delete clicked')}
                        />
                        <ImageItem 
                            imageUrl="https://picsum.photos/253/100?random=2"
                            imageAlt="Sample image 2"
                            date="02.02.25"
                            device="android"
                            onCopy={(imageUrl) => console.log('Copied image URL:', imageUrl)}
                            onEdit={() => console.log('Edit clicked')}
                            onDelete={() => console.log('Delete clicked')}
                        />
                        <ImageItem 
                            imageUrl="https://picsum.photos/253/100?random=3"
                            imageAlt="Sample image 3"
                            date="02.02.25"
                            device="apple"
                            onCopy={(imageUrl) => console.log('Copied image URL:', imageUrl)}
                            onEdit={() => console.log('Edit clicked')}
                            onDelete={() => console.log('Delete clicked')}
                        />
                    </div>
                </div>
            </div>

        </div>
    );
}



